/**
 * Résolution du tenant (domaine) actif de l'utilisateur courant.
 *
 * Source de vérité = l'objet `auth.me`. La logique était dupliquée inline dans
 * `AppShellLayout` ; on l'extrait ici pour la partager avec la couche offline
 * (clé de scoping du cache parcelles) — règle DRY (CLAUDE.md).
 *
 * Règle de résolution (identique à l'historique AppShellLayout) :
 *   1. La membership dont `domaine.id === domaine_id_actif` (cas nominal :
 *      l'utilisateur a un domaine actif posé en session).
 *   2. À défaut (mono-tenant où `domaine_id_actif` peut être `null`), l'unique
 *      membership disponible.
 *   3. Sinon `null` (aucun domaine résolu → on ne suppose RIEN).
 *
 * Le typage est STRUCTUREL et générique : on n'exige que les champs
 * strictement nécessaires (`domaine.id`), ce qui permet de passer l'objet
 * `me` complet (typage inféré tRPC) sans couplage sur son type exact.
 */

/** Contrainte minimale d'une membership : on ne lit que l'id du domaine. */
interface MembershipAvecDomaineId {
  domaine: { id: string };
}

/** Contexte tenant minimal extrait de `auth.me` (typage structurel). */
export interface MeTenantContext<M extends MembershipAvecDomaineId = MembershipAvecDomaineId> {
  domaine_id_actif: string | null;
  domaines_accessibles: readonly M[];
}

/**
 * Retourne la membership active de l'utilisateur, ou `null`.
 * Générique : préserve le type concret de la membership pour l'appelant.
 */
export function resoudreMembershipActive<M extends MembershipAvecDomaineId>(
  me: MeTenantContext<M> | null | undefined,
): M | null {
  if (!me) return null;
  if (me.domaine_id_actif) {
    // Un id actif incohérent ne doit jamais retomber silencieusement sur un
    // autre client : la session devra repasser par le choix de domaine.
    return me.domaines_accessibles.find((m) => m.domaine.id === me.domaine_id_actif) ?? null;
  }
  // Le repli n'est sûr que lorsqu'il n'existe qu'une seule possibilité.
  return me.domaines_accessibles.length === 1 ? (me.domaines_accessibles[0] ?? null) : null;
}

/**
 * Retourne l'`id` du domaine actif, ou `null` si aucun domaine n'est résolu.
 *
 * C'est la CLÉ de scoping du cache parcelles hors-ligne : lecture et écriture
 * du cache passent toutes deux par cette même résolution → cohérence garantie
 * (on écrit sous la clé X, on relit sous la clé X). Un `null` ici fait
 * échouer le cache en « fermé » (aucune parcelle servie) plutôt que de risquer
 * de servir la liste d'un autre tenant (cf règle projet #3).
 */
export function resoudreDomaineIdActif(me: MeTenantContext | null | undefined): string | null {
  return resoudreMembershipActive(me)?.domaine.id ?? null;
}
